import { useEffect, useRef, useState } from "react";
import { Button, Form, Modal, Spinner } from "react-bootstrap";
import { differenceInCalendarDays, format, subDays } from "date-fns";
import { useTranslations } from "use-intl";
import { useSubAccount } from "@/hooks";
import { PickDate } from "@/features/ui/pick-date";
import style from "./index.module.scss";
import {
  createBulkExport,
  downloadBulkExport,
  getBulkExportJob,
} from "@/store/sub_accounts/api";

interface IProps {
  kind: "earnings" | "payouts";
}

const POLL_INTERVAL_MS = 2000;
const POLL_TIMEOUT_MS = 10 * 60 * 1000;
const DATE_FORMAT = "yyyy-MM-dd";
// Same limit as the backend (cabinet_export_service.MAX_PERIOD_DAYS).
const MAX_PERIOD_DAYS = 60;
// Large clients have hundreds of sub-accounts: past this a search box helps.
const SEARCH_FROM = 10;

const sleep = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

// Export of earnings / payouts for all sub-accounts or a chosen subset.
// The file is built by a backend job, so a large period is fine to request:
// the backend refuses a selection that would be too big and says why.
export function BulkExport({ kind }: IProps) {
  const i18n = useTranslations("cabinet.bulkExport");
  const { subAccounts } = useSubAccount();
  const [show, setShow] = useState(false);
  const [selected, setSelected] = useState<string[]>([]);
  const [search, setSearch] = useState("");
  const [date, setDate] = useState({ from: subDays(new Date(), 30), to: new Date() });
  const [running, setRunning] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const aborted = useRef(false);
  const controller = useRef<AbortController | null>(null);

  useEffect(() => {
    aborted.current = false;
    return () => {
      aborted.current = true;
      controller.current?.abort();
    };
  }, []);

  const allSelected = selected.length === 0;
  const toggle = (name: string) =>
    setSelected((prev) =>
      prev.includes(name) ? prev.filter((n) => n !== name) : [...prev, name],
    );

  const errorText = (e: any): string => {
    const data = e?.response?.data;
    if (e?.response?.status === 429) {
      return i18n("alreadyRunning");
    }
    if (data?.error_code === "EXPORT_TOO_LARGE") {
      return i18n("tooLarge");
    }
    if (data?.error_code === "PERIOD_TOO_LONG") {
      return i18n("periodTooLong");
    }
    return data?.detail || i18n("failed");
  };

  const run = async () => {
    setError(null);
    if (differenceInCalendarDays(date.to, date.from) + 1 > MAX_PERIOD_DAYS) {
      setError(i18n("periodTooLong"));
      return;
    }
    setRunning(true);
    controller.current = new AbortController();
    try {
      const { data: created } = await createBulkExport({
        kind,
        sub_account_names: selected,
        start_date: format(date.from, DATE_FORMAT),
        end_date: format(date.to, DATE_FORMAT),
      });
      const deadline = Date.now() + POLL_TIMEOUT_MS;
      while (!aborted.current) {
        const { data: job } = await getBulkExportJob(created.job_id, {
          signal: controller.current.signal,
        });
        if (job.status === "FAILED") {
          throw new Error(i18n("failed"));
        }
        if (job.status === "DONE" && job.download_ready) {
          await downloadBulkExport(created.job_id);
          setShow(false);
          return;
        }
        if (Date.now() > deadline) {
          throw new Error(i18n("timeout"));
        }
        await sleep(POLL_INTERVAL_MS);
      }
    } catch (e: any) {
      if (!aborted.current) {
        setError(e?.response ? errorText(e) : e?.message || i18n("failed"));
      }
    } finally {
      if (!aborted.current) {
        setRunning(false);
      }
    }
  };

  return (
    <>
      <Button variant="outline-primary" onClick={() => setShow(true)}>
        {i18n("button")}
      </Button>
      <Modal
        show={show}
        onHide={() => !running && setShow(false)}
        centered
        dialogClassName={style.modal}
      >
        <Modal.Header closeButton={!running}>
          <Modal.Title>{i18n(`title.${kind}`)}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p className="mb-2">{i18n("subAccounts")}</p>
          <Form.Check
            type="checkbox"
            id="bulk-export-all"
            label={i18n("all")}
            checked={allSelected}
            disabled={running}
            onChange={() => setSelected([])}
          />
          {subAccounts.length > SEARCH_FROM && (
            <Form.Control
              className="my-2"
              size="sm"
              placeholder={i18n("search")}
              value={search}
              disabled={running}
              onChange={(e) => setSearch(e.target.value)}
            />
          )}
          <div className={style.list}>
            {subAccounts
              .filter((account: any) =>
                account.name.toLowerCase().includes(search.trim().toLowerCase()),
              )
              .map((account: any) => (
                <Form.Check
                  type="checkbox"
                  key={account.name}
                  id={`bulk-export-${account.name}`}
                  label={`${account.name} (${account.crypto_currency})`}
                  checked={selected.includes(account.name)}
                  disabled={running}
                  onChange={() => toggle(account.name)}
                />
              ))}
          </div>
          <p className="mt-3 mb-2">{i18n("period")}</p>
          <PickDate defaultDate={date} onChange={setDate} />
          <p className="text-muted mt-3 mb-0">{i18n("hint")}</p>
          {error && <p className="text-danger mt-3 mb-0">{error}</p>}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" disabled={running} onClick={() => setShow(false)}>
            {i18n("cancel")}
          </Button>
          <Button onClick={run} disabled={running}>
            {running ? <Spinner size="sm" animation="border" /> : i18n("export")}
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
}
